import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import LandingPage from './components/LandingPage';
import Workspace from './components/Workspace';
import SignIn from './components/SignIn';
import Dashboard from './components/Dashboard';
import APIDocsPageSEO from './pages/APIDocsPageSEO';
import DevToolsHub from './pages/DevToolsHub';
import JWTDebuggerPage from './pages/JWTDebuggerPage';
import JSONFormatterPage from './pages/JSONFormatterPage';
import Base64ToolPage from './pages/Base64ToolPage';
import TimestampConverterPage from './pages/TimestampConverterPage';
import URLToolPage from './pages/URLToolPage';
import UUIDGeneratorPage from './pages/UUIDGeneratorPage';
import HashGeneratorPage from './pages/HashGeneratorPage';
import MockDataGeneratorPage from './pages/MockDataGeneratorPage';
import YAMLConverterPage from './pages/YAMLConverterPage';
import ENVConverterPage from './pages/ENVConverterPage';
import BlogIndexPage from './pages/BlogIndexPage';
import BlogPostPage from './pages/BlogPostPage';
import SEO from './components/SEO';

function App() {
  const handleBack = () => {
    window.location.href = '/';
  };

  const handleGetStarted = () => {
    window.location.href = '/playground';
  };

  const handleOpenAPIDocs = () => {
    window.location.href = '/docs';
  };

  const handleOpenDashboard = async () => {
    try {
      const response = await fetch('/api/auth/session');
      const data = await response.json();
      if (data.user) {
        window.location.href = '/dashboard';
      } else {
        window.location.href = '/signin';
      }
    } catch (error) {
      window.location.href = '/signin';
    }
  };

  const handleOpenDevTools = () => {
    window.location.href = '/dev-tools';
  };

  return (
    <Router>
      <div className="min-h-screen">
        <Routes>
          {/* Home Page */}
          <Route
            path="/"
            element={
              <>
                <SEO
                  title="JsonToonCraft - Your Complete Developer Toolkit | JSON Converter, Mock APIs & Dev Tools"
                  description="Convert JSON to TypeScript, Zod, OpenAPI & TOON. Generate mock REST APIs with 8 templates. Access 15+ developer tools including JWT debugger, UUID generator, Base64 encoder. Everything you need to build and ship faster."
                  keywords="JSON to TypeScript, JSON converter, Mock API generator, Zod schema, OpenAPI, TOON format, developer tools, JWT debugger, Base64, UUID generator, hash generator, developer toolkit"
                  canonicalUrl="https://jsontooncraft.com"
                  structuredData={{
                    "@context": "https://schema.org",
                    "@type": "WebApplication",
                    "name": "JsonToonCraft",
                    "applicationCategory": "DeveloperApplication",
                    "operatingSystem": "Web",
                    "offers": {
                      "@type": "Offer",
                      "price": "0",
                      "priceCurrency": "USD"
                    },
                    "description": "Your complete developer toolkit. Convert JSON to any format, generate mock REST APIs, and access 15+ essential developer tools — all in one place.",
                    "featureList": [
                      "JSON to TypeScript converter",
                      "Zod schema generator",
                      "OpenAPI 3.1 schema generator",
                      "TOON format converter (30-60% token savings)",
                      "Mock API generator with 8 templates",
                      "15+ developer tools (JWT, Base64, UUID, Hash, etc.)",
                      "REST API access with 10K free requests/month",
                      "No CORS restrictions",
                      "Built-in analytics dashboard"
                    ]
                  }}
                />
                <LandingPage
                  onGetStarted={handleGetStarted}
                  onOpenAPIDocs={handleOpenAPIDocs}
                  onOpenDashboard={handleOpenDashboard}
                  onOpenDevTools={handleOpenDevTools}
                />
              </>
            }
          />

          {/* Playground */}
          <Route path="/playground" element={<Workspace onBack={handleBack} openDevTools={false} />} />

          {/* API Documentation */}
          <Route path="/docs" element={<APIDocsPageSEO />} />

          {/* Sign In */}
          <Route path="/signin" element={<SignIn onBack={handleBack} />} />

          {/* Dashboard */}
          <Route path="/dashboard" element={<Dashboard onBack={handleBack} />} />

          {/* Developer Tools Hub */}
          <Route path="/dev-tools" element={<DevToolsHub />} />

          {/* Individual Tool Pages */}
          <Route path="/dev-tools/jwt-debugger" element={<JWTDebuggerPage />} />
          <Route path="/dev-tools/json-formatter" element={<JSONFormatterPage />} />
          <Route path="/dev-tools/base64" element={<Base64ToolPage />} />
          <Route path="/dev-tools/timestamp-converter" element={<TimestampConverterPage />} />
          <Route path="/dev-tools/url-encoder" element={<URLToolPage />} />
          <Route path="/dev-tools/uuid-generator" element={<UUIDGeneratorPage />} />
          <Route path="/dev-tools/hash-generator" element={<HashGeneratorPage />} />
          <Route path="/dev-tools/mock-data-generator" element={<MockDataGeneratorPage />} />
          <Route path="/dev-tools/yaml-converter" element={<YAMLConverterPage />} />
          <Route path="/dev-tools/env-converter" element={<ENVConverterPage />} />

          {/* Blog Pages */}
          <Route path="/blog" element={<BlogIndexPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Analytics />
        <SpeedInsights />
      </div>
    </Router>
  );
}

export default App;
